// Shared alkane-ladder editor. Two modes:
//   - "config": uploads against an existing user_exp_config — used by the
//     drawer's edit mode and the expanded-card inline upload on the
//     Manage Chromatography page.
//   - "draft":  uploads against a client-generated draft_id BEFORE the
//     user_exp_config exists — used by the drawer's new mode. The parent
//     drawer holds the draft state in component memory and passes the
//     accumulated file_upload_ids to createConfig at Register time. On
//     cancel/close, the drawer fires abandonDraftLadders(draftId) to
//     mark the in-flight file_uploads as 'abandoned' so they don't
//     linger as orphans.
//
// Server-side flow (both modes): pick → presign → S3 PUT (with progress)
// → complete. In config mode the complete endpoint inserts a
// user_exp_config_alkane_ladder row; in draft mode it just flips the
// file_upload to 'uploaded' and waits for the Register-create handler's
// Stage 3b to claim the row onto the new config.
import { useCallback, useRef, useState } from "react";
import { Icon } from "@/components/Icon/Icon";
import {
  abandonDraftLadder,
  archiveLadder,
  completeDraftLadder,
  completeLadder,
  presignDraftLadder,
  presignLadder,
  type LadderRow,
} from "@/services/instruments_api";
import { putFileToS3 } from "@/services/uploads_api";

type UploadingLadder = {
  tempId: string;
  filename: string;
  sizeBytes: number;
  contentType: string;
  status: "presigning" | "uploading" | "verifying" | "failed";
  progress: number;
  errorMessage?: string;
};

export type DraftLadderUpload = {
  fileUploadId: string;
  filename: string;
  sizeBytes: number;
  uploadedAt: string;
};

const LADDER_EXTENSIONS = [".raw", ".mzml", ".csv", ".xlsx"] as const;
function hasAllowedExtension(filename: string): boolean {
  const lower = filename.toLowerCase();
  return LADDER_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

// Browsers (notably Windows + Edge / Chrome) report misleading MIME
// types for vendor binary chromatogram files — e.g. file.type ==
// "image/RAW" for an Agilent .raw, which the server then rejects as
// not-on-allowlist. Derive content_type from the extension so we always
// send a value the server's kind='alkane_ladder' allowlist accepts.
function ladderContentType(filename: string): string {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".csv")) return "text/csv";
  if (lower.endsWith(".xlsx")) {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  // .raw and .mzML are vendor binary — generic octet-stream is on the
  // server allowlist and avoids the image/RAW false-positive.
  return "application/octet-stream";
}

export type AlkaneLadderEditorProps =
  | {
      mode: "config";
      configId: string;
      serverLadders: LadderRow[];
      onMutated: () => void;
    }
  | {
      mode: "draft";
      draftId: string;
      drafts: DraftLadderUpload[];
      onDraftsChange: (next: DraftLadderUpload[]) => void;
    };

export function AlkaneLadderEditor(props: AlkaneLadderEditorProps) {
  const [uploading, setUploading] = useState<UploadingLadder[]>([]);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handlePick = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files) return;
      const list = Array.from(files);
      for (const file of list) {
        if (!hasAllowedExtension(file.name)) {
          setUploading((u) => [
            ...u,
            {
              tempId: `bad-${Date.now()}-${Math.random()}`,
              filename: file.name,
              sizeBytes: file.size,
              contentType: file.type,
              status: "failed",
              progress: 0,
              errorMessage: "Unsupported extension (.raw / .mzML / .csv / .xlsx)",
            },
          ]);
          continue;
        }
        const tempId = `up-${Date.now()}-${Math.random()}`;
        setUploading((u) => [
          ...u,
          {
            tempId,
            filename: file.name,
            sizeBytes: file.size,
            contentType: ladderContentType(file.name),
            status: "presigning",
            progress: 0,
          },
        ]);
        try {
          const contentType = ladderContentType(file.name);
          const presign =
            props.mode === "config"
              ? await presignLadder(props.configId, {
                  filename: file.name,
                  content_type: contentType,
                  size_bytes: file.size,
                })
              : await presignDraftLadder({
                  draft_id: props.draftId,
                  filename: file.name,
                  content_type: contentType,
                  size_bytes: file.size,
                });
          setUploading((u) =>
            u.map((r) => (r.tempId === tempId ? { ...r, status: "uploading" } : r)),
          );
          await putFileToS3({
            url: presign.presigned_url,
            file,
            requiredHeaders: presign.required_headers,
            onProgress: (pct) => {
              setUploading((u) =>
                u.map((r) => (r.tempId === tempId ? { ...r, progress: pct } : r)),
              );
            },
          });
          setUploading((u) =>
            u.map((r) => (r.tempId === tempId ? { ...r, status: "verifying" } : r)),
          );
          if (props.mode === "config") {
            await completeLadder(props.configId, presign.file_upload_id);
            setUploading((u) => u.filter((r) => r.tempId !== tempId));
            props.onMutated();
          } else {
            const completed = await completeDraftLadder(presign.file_upload_id);
            setUploading((u) => u.filter((r) => r.tempId !== tempId));
            props.onDraftsChange([
              ...props.drafts,
              {
                fileUploadId: completed.file_upload_id,
                filename: file.name,
                sizeBytes: file.size,
                uploadedAt: completed.uploaded_at,
              },
            ]);
          }
        } catch {
          // Sanitised — no upstream detail rendered.
          setUploading((u) =>
            u.map((r) =>
              r.tempId === tempId
                ? { ...r, status: "failed", errorMessage: "Upload failed" }
                : r,
            ),
          );
        }
      }
    },
    [props],
  );

  const handleRemoveServer = useCallback(
    async (ladderId: string) => {
      if (props.mode !== "config") return;
      setArchivingId(ladderId);
      try {
        await archiveLadder(props.configId, ladderId);
        props.onMutated();
      } finally {
        setArchivingId(null);
      }
    },
    [props],
  );

  const handleRemoveDraft = useCallback(
    async (fileUploadId: string) => {
      if (props.mode !== "draft") return;
      setArchivingId(fileUploadId);
      try {
        await abandonDraftLadder(fileUploadId);
        props.onDraftsChange(
          props.drafts.filter((d) => d.fileUploadId !== fileUploadId),
        );
      } finally {
        setArchivingId(null);
      }
    },
    [props],
  );

  const handleRemoveUploading = (tempId: string) =>
    setUploading((u) => u.filter((r) => r.tempId !== tempId));

  // Rows the user has committed (either server-attached in config mode,
  // or buffered drafts in draft mode). Rendered identically aside from
  // the pill text and the remove handler.
  type Row = {
    key: string;
    id: string;
    filename: string;
    sizeBytes: number;
    uploadedAt: string | null;
    pillText: "Calibrated" | "Pending";
    onRemove: (id: string) => void | Promise<void>;
  };
  const rows: Row[] =
    props.mode === "config"
      ? props.serverLadders.map((l) => ({
          key: l.ladder_id,
          id: l.ladder_id,
          filename: l.original_filename,
          sizeBytes: l.size_bytes,
          uploadedAt: l.uploaded_at,
          pillText: "Calibrated" as const,
          onRemove: handleRemoveServer,
        }))
      : props.drafts.map((d) => ({
          key: d.fileUploadId,
          id: d.fileUploadId,
          filename: d.filename,
          sizeBytes: d.sizeBytes,
          uploadedAt: d.uploadedAt,
          pillText: "Pending" as const,
          onRemove: handleRemoveDraft,
        }));

  const totalCount = rows.length;
  const uploadDisabled = false;

  return (
    <div className="vstack" style={{ gap: 8 }}>
      {rows.map((r) => {
        const isArchiving = archivingId === r.id;
        return (
          <div
            key={r.key}
            className="hstack"
            style={{
              gap: 10,
              padding: "9px 12px",
              borderRadius: 6,
              background: "var(--bg-sunk)",
              border: "1px solid var(--line)",
              opacity: isArchiving ? 0.55 : 1,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 5,
                flexShrink: 0,
                background: "var(--panel)",
                border: "1px solid var(--line)",
                color: "var(--ink-3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="molecule" size={13} />
            </div>
            <div
              className="vstack"
              style={{ flex: 1, gap: 2, minWidth: 0, lineHeight: 1.3 }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--ink)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={r.filename}
              >
                {r.filename}
              </span>
              <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                {(r.sizeBytes / (1024 * 1024)).toFixed(1)} MB
                {r.uploadedAt ? ` · uploaded ${r.uploadedAt.slice(0, 10)}` : ""}
              </span>
            </div>
            <span
              className="mono"
              style={{
                fontSize: 9,
                fontWeight: 700,
                padding: "2px 6px",
                borderRadius: 3,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                background:
                  r.pillText === "Pending" ? "var(--bg-sunk)" : "var(--accent-soft)",
                color:
                  r.pillText === "Pending" ? "var(--ink-3)" : "var(--accent-ink)",
                border: `1px solid ${r.pillText === "Pending" ? "var(--line)" : "var(--accent-line)"}`,
              }}
            >
              {r.pillText}
            </span>
            <button
              className="btn xs ghost"
              onClick={() => r.onRemove(r.id)}
              disabled={isArchiving}
              title={r.pillText === "Pending" ? "Discard upload" : "Detach ladder"}
            >
              <Icon name="x" size={11} />
            </button>
          </div>
        );
      })}

      {uploading.map((u) => (
        <div
          key={u.tempId}
          className="hstack"
          style={{
            gap: 10,
            padding: "9px 12px",
            borderRadius: 6,
            background: "var(--bg-sunk)",
            border: `1px solid ${u.status === "failed" ? "rgba(161, 75, 58, 0.4)" : "var(--line)"}`,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 5,
              flexShrink: 0,
              background: "var(--panel)",
              border: "1px solid var(--line)",
              color: u.status === "failed" ? "#a14b3a" : "var(--ink-3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name={u.status === "failed" ? "alert" : "molecule"} size={13} />
          </div>
          <div
            className="vstack"
            style={{ flex: 1, gap: 4, minWidth: 0, lineHeight: 1.3 }}
          >
            <span
              className="mono"
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "var(--ink)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={u.filename}
            >
              {u.filename}
            </span>
            {u.status === "failed" ? (
              <span className="mono" style={{ fontSize: 10, color: "#a14b3a" }}>
                {u.errorMessage ?? "Upload failed"}
              </span>
            ) : (
              <div className="vstack" style={{ gap: 2 }}>
                <span className="mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
                  {u.status === "presigning"
                    ? "Preparing upload…"
                    : u.status === "uploading"
                      ? `Uploading… ${u.progress}%`
                      : "Verifying…"}
                </span>
                <div
                  style={{
                    height: 3,
                    borderRadius: 2,
                    background: "var(--line)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${u.status === "verifying" ? 100 : u.progress}%`,
                      height: "100%",
                      background: "var(--brand-green)",
                      transition: "width 120ms ease",
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          {u.status === "failed" && (
            <button
              className="btn xs ghost"
              onClick={() => handleRemoveUploading(u.tempId)}
              title="Dismiss"
            >
              <Icon name="x" size={11} />
            </button>
          )}
        </div>
      ))}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".raw,.mzML,.mzml,.csv,.xlsx"
        style={{ display: "none" }}
        onChange={(e) => {
          handlePick(e.target.files);
          // Allow re-selecting the same file later.
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadDisabled}
        onDragOver={(e) => {
          if (uploadDisabled) return;
          e.preventDefault();
          e.stopPropagation();
          if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
          if (!dragOver) setDragOver(true);
        }}
        onDragEnter={(e) => {
          if (uploadDisabled) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOver(true);
        }}
        onDragLeave={(e) => {
          // Only clear when the pointer actually leaves the button (not when
          // it just crosses a child element). relatedTarget is null when
          // leaving the browser window.
          const next = e.relatedTarget as Node | null;
          if (next && e.currentTarget.contains(next)) return;
          setDragOver(false);
        }}
        onDrop={(e) => {
          if (uploadDisabled) return;
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          const dropped = e.dataTransfer?.files;
          if (dropped && dropped.length > 0) {
            handlePick(dropped);
          }
        }}
        style={{
          width: "100%",
          padding: "14px 16px",
          borderRadius: 8,
          border: `1.5px dashed ${dragOver ? "var(--brand-green)" : "var(--line-strong)"}`,
          background: dragOver ? "var(--accent-soft)" : "var(--bg-sunk)",
          color: "var(--ink-3)",
          textAlign: "center",
          cursor: uploadDisabled ? "not-allowed" : "pointer",
          opacity: uploadDisabled ? 0.5 : 1,
          transition: "background 100ms ease, border-color 100ms ease",
        }}
      >
        <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--ink-2)" }}>
          {dragOver
            ? "Drop to attach"
            : totalCount === 0
              ? "Upload alkane ladder run"
              : "Upload another ladder"}
        </div>
        <div className="mono" style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 4 }}>
          .raw · .mzML · .csv · .xlsx · linearizes retention indices for this configuration
        </div>
      </button>
    </div>
  );
}
